import React, { useEffect, useState } from 'react';
import { Modal, View } from 'react-native';
import { translate } from '../../services/translate';
import MyAppText from '../../components/MyAppText';
import Toast from 'react-native-toast-message';
import { ClientError } from '../../services/central-api/base-service';
import PasswordValidator from 'password-validator';
import PasswordInput from '../../components/PasswordInput';
import { centralAPI } from '../../services/central-api';
import CustomButton from '../../components/CustomButton';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import Container from '../../components/Container';
import Card from '../../components/Card';
import Header from '../../components/Header';
import { forceRedirectToLogin } from '../../services/navigation-assistant';

export default function Password() {
    const { styles } = useStyles(styleSheet);
    const [isFormValid, setIsFormValid] = useState(false);
    const [isModalVisible, setModalVisible] = useState(false);
    const [isValidMin, setIsValidMin] = useState(false);
    const [isValidDigits, setIsValidDigits] = useState(false);
    const [isValidUppercase, setIsValidUppercase] = useState(false);
    const [isValidLowercase, setIsValidLowercase] = useState(false);

    const validator = new PasswordValidator();
    validator.is().min(8).has().uppercase().has().lowercase().has().digits(2);

    const [oldPassword, setOldPassword] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');


    useEffect(() => {
        if (oldPassword === '' || password === '' || confirmPassword === '') {
            return setIsFormValid(false);
        }

        if (confirmPassword !== password && (oldPassword !== '' || password !== '' || confirmPassword !== '')) {
            return setIsFormValid(false);
        }

        if (!validator.validate(password)) {
            return setIsFormValid(false);
        }

        return setIsFormValid(true);
    }, [oldPassword, password, confirmPassword]);

    useEffect(() => {
        const missingValidations = validator.validate(password, { list: true }) as string[];
        setIsValidMin(!missingValidations.includes('min'));
        setIsValidUppercase(!missingValidations.includes('uppercase'));
        setIsValidLowercase(!missingValidations.includes('lowercase'));
        setIsValidDigits(!missingValidations.includes('digits'));
    }, [password]);

    async function save() {
        try {
            await centralAPI.updateOwnUser({
                oldPassword,
                password,
                confirmPassword
            });
            Toast.show({
                type: 'sentinelxSuccess',
                text1: translate('ActionSuccessfully'),
            });
            forceRedirectToLogin();

        } catch (err) {
            if (err instanceof ClientError) {
                return Toast.show({
                    type: 'sentinelxError',
                    text1: translate(err.message),
                });
            }
            console.error(err);
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
        }
    }

    return (
        <Container>
            <Header
                title={translate('changePassword')}
                actions={<CustomButton
                    onPress={() => setModalVisible(true)}
                    disabled={!isFormValid}
                    text={translate('save')}
                />}
            />
            <Card scrollView={true}>
                <View style={{ rowGap: 20, maxWidth: 500 }}>
                    <PasswordInput
                        label={translate('oldPassword')}
                        placeholder=''
                        value={oldPassword}
                        onChangeText={setOldPassword}
                        invalid={() => false}
                    />
                    <View style={{ gap: 10 }}>
                        <MyAppText style={[styles.label, { fontSize: 16 }]}>{translate('passwordRequirements')}</MyAppText>
                        <View style={{ gap: 5 }}>
                            <MyAppText style={[styles.label, password === '' ? null : isValidMin ? styles.valid : styles.invalid]}>{`\u2022 ${translate('atLeast8Characters')}`}</MyAppText>
                            <MyAppText style={[styles.label, password === '' ? null : isValidDigits ? styles.valid : styles.invalid]}>{`\u2022 ${translate('atLeast2Digits')}`}</MyAppText>
                            <MyAppText style={[styles.label, password === '' ? null : isValidUppercase ? styles.valid : styles.invalid]}>{`\u2022 ${translate('atLeastOneUppercase')}`}</MyAppText>
                            <MyAppText style={[styles.label, password === '' ? null : isValidLowercase ? styles.valid : styles.invalid]}>{`\u2022 ${translate('atLeastOneLowercase')}`}</MyAppText>
                        </View>
                    </View>
                    <PasswordInput
                        label={translate('newPassword')}
                        placeholder=''
                        value={password}
                        onChangeText={setPassword}
                        invalid={() => false}
                    />
                    <PasswordInput
                        label={translate('confirmNewPassword')}
                        placeholder=''
                        value={confirmPassword}
                        onChangeText={setConfirmPassword}
                        invalid={() => password != confirmPassword}
                        errorMessage={translate('passwordsNotEqual')}
                    />
                </View>
            </Card>
            <Modal visible={isModalVisible} animationType='fade' onRequestClose={() => setModalVisible(false)} transparent={true}>
                <View style={styles.modalContainer}>
                    <View style={styles.modalBody}>
                        <MyAppText style={styles.confirmationText}>{translate('resetPasswordClearSessions')}</MyAppText>
                        <View style={styles.buttonsView}>
                            <CustomButton
                                onPress={() => setModalVisible(false)}
                                text={translate('cancel')}
                                type='secondary'
                            />
                            <CustomButton
                                onPress={() => {
                                    save();
                                    setModalVisible(false);
                                }}
                                text={translate('confirm')}
                            />
                        </View>
                    </View>
                </View>
            </Modal>
        </Container>
    );
}

const styleSheet = createStyleSheet((theme) => ({
    header: {
        flexDirection: 'row',
        alignItems: 'flex-end',
        columnGap: 10,
        minHeight: 65,
        justifyContent: 'flex-end',
    },
    headerText: {
        fontSize: 24,
        color: theme.colors.labelColor,
        fontWeight: 'bold'
    },
    buttonDisabled: {
        borderWidth: 0,
        backgroundColor: theme.colors.disabledText
    },
    textDisabled: {
        color: theme.colors.disabledText
    },
    confirmationText: {
        fontSize: 20
    },
    modalContainer: {
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: '#31313199',
        flex: 1
    },
    modalBody: {
        backgroundColor: theme.colors.card.background,
        flexDirection: 'column',
        borderRadius: 4,
        padding: 25,
        rowGap: 20
    },
    buttonClose: {
        borderWidth: 1,
        borderColor: theme.colors.card.border,
        backgroundColor: theme.colors.card.background,
    },
    buttonTextClose: {
        fontSize: 16,
        color: theme.colors.labelColor,
    },
    buttonsView: {
        flexDirection: 'row',
        alignItems: 'center',
        flexWrap: 'wrap',
        justifyContent: 'flex-end',
        gap: 20
    },
    button: {
        borderRadius: 4,
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        width: 120,
        height: 40,
        backgroundColor: theme.colors.primaryButton.background,
    },
    buttonTextDelete: {
        fontSize: 16,
        color: theme.colors.secondaryButton.background,
    },
    label: {
        color: theme.colors.labelColor,
    },
    valid: {
        color: theme.colors.successes['300'],
    },
    invalid: {
        color: theme.colors.errors['300'],
    }
}));
